import * as React from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { coerceKpiNumber } from "@/lib/coerce-numeric";
import type { OeeResponse } from "@opus-dt/shared-types";
import type {
  DashboardDensity,
  DashboardOverview,
  DashboardSort,
  DashboardStatusFilter,
  DashboardViewState,
} from "@/types/dashboard.types";

export function useDashboardOverview() {
  return useQuery<DashboardOverview>({
    queryKey: ["dashboard", "overview"],
    queryFn: () => api.get<DashboardOverview>("/api/v1/dashboard/overview"),
    select: (data) => ({
      ...data,
      oeeAverage24h: coerceKpiNumber(data.oeeAverage24h, "dashboard.oeeAverage24h"),
    }),
    refetchInterval: 60_000,
  });
}

export interface UseDashboardViewState extends DashboardViewState {
  setStatusFilter: (f: DashboardStatusFilter) => void;
  setQuery: (q: string) => void;
  toggleResponsible: (r: string) => void;
  clearResponsible: () => void;
  setSort: (s: DashboardSort) => void;
  setDensity: (d: DashboardDensity, visibleIds: string[]) => void;
  toggleExpand: (id: string) => void;
  toggleSelect: (id: string) => void;
  clearSelection: () => void;
}

export interface LineOeeSummary {
  oee: number | null;
  availability: number | null;
  performance: number | null;
  quality: number | null;
  planAdherence: number | null;
}

export function useLinesOeeMap(lineIds: string[]): Record<string, LineOeeSummary> {
  const results = useQueries({
    queries: lineIds.map((id) => ({
      queryKey: ["oee", id, "24h", false, true] as const,
      queryFn: () => api.get<OeeResponse>(`/api/v1/lines/${id}/oee?period=24h&breakdown=true`),
      staleTime: 60_000,
    })),
  });
  return React.useMemo(() => {
    const map: Record<string, LineOeeSummary> = {};
    lineIds.forEach((id, i) => {
      const d = results[i]?.data;
      map[id] = {
        oee: d?.oee ?? null,
        availability: d?.availability ?? null,
        performance: d?.performance ?? null,
        quality: d?.quality ?? null,
        planAdherence: d?.planAdherence?.value ?? null,
      };
    });
    return map;
  }, [lineIds, results]);
}

function toggleInSet(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export function useDashboardViewState(): UseDashboardViewState {
  const [statusFilter, setStatusFilter] = React.useState<DashboardStatusFilter>("todas");
  const [query, setQuery] = React.useState("");
  const [responsibleFilter, setResponsibleFilter] = React.useState<Set<string>>(() => new Set());
  const [sort, setSort] = React.useState<DashboardSort>("name");
  const [density, setDensityState] = React.useState<DashboardDensity>("compact");
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => new Set());
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());

  const toggleResponsible = React.useCallback(
    (r: string) => setResponsibleFilter((prev) => toggleInSet(prev, r)),
    []
  );
  const clearResponsible = React.useCallback(() => setResponsibleFilter(new Set()), []);
  const toggleExpand = React.useCallback(
    (id: string) => setExpandedIds((prev) => toggleInSet(prev, id)),
    []
  );
  const toggleSelect = React.useCallback(
    (id: string) => setSelectedIds((prev) => toggleInSet(prev, id)),
    []
  );
  const clearSelection = React.useCallback(() => setSelectedIds(new Set()), []);
  const setDensity = React.useCallback((d: DashboardDensity, visibleIds: string[]) => {
    setDensityState(d);
    setExpandedIds(d === "expanded" ? new Set(visibleIds) : new Set());
  }, []);

  return {
    statusFilter,
    query,
    responsibleFilter,
    sort,
    density,
    expandedIds,
    selectedIds,
    setStatusFilter,
    setQuery,
    toggleResponsible,
    clearResponsible,
    setSort,
    setDensity,
    toggleExpand,
    toggleSelect,
    clearSelection,
  };
}
